/**
 * Cash Flow Statement in the standard indirect layout (IAS 7 / FRS 102 s7): profit, non-cash
 * adjustments, working capital, investing, financing, opening → closing cash. The direct-method
 * view (cash by counterparty) is kept below as supporting detail.
 * POST /api/financial-config/cash-flow/generate
 *
 * Categories: operating, investing, financing, unclassified. Opening + net change = closing cash,
 * checked for both methods (tie_check); the indirect method starts from GL net income.
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Alert, CircularProgress, Stack, Card, CardContent,
  Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Divider, IconButton, Tooltip, Collapse,
} from '@mui/material';
import {
  Refresh, TrendingUp, TrendingDown, SwapHoriz, ExpandMore, ChevronRight,
  ManageSearch as DrillIcon,
} from '@mui/icons-material';
import { generateCashFlowStatement } from '../../services/financialConfigService';
import StatementTable from './StatementTable';
import AccountEntriesDialog from './AccountEntriesDialog';

const fmtSigned = (n) => {
  const v = Number(n || 0);
  return (v < 0 ? '-' : '') + '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function CategoryCard({ title, icon, bucket, excluded, color, onDrillAccount }) {
  const lines = bucket?.lines || [];
  return (
    <Card sx={{ mb: 2, opacity: excluded ? 0.7 : 1 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {icon}
            <Typography variant="h6">{title}</Typography>
            {excluded && <Chip size="small" label="Excluded from net" color="warning" variant="outlined" />}
          </Stack>
          <Typography variant="h6" sx={{ color, fontWeight: 700 }}>
            {fmtSigned(bucket?.net || 0)}
          </Typography>
        </Stack>
        {lines.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>Counterparty</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>Code</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>JEs</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>Net (USD)</TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>Fix</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.slice(0, 12).map((l, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontSize: '0.85rem' }}>{l.counter_name || l.counter_subtype || '(none)'}</TableCell>
                    <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{l.counter_code}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.85rem' }}>{l.je_count}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontSize: '0.9rem', color: l.net_usd < 0 ? 'error.main' : 'success.main', fontWeight: 500 }}
                    >
                      {fmtSigned(l.net_usd)}
                    </TableCell>
                    <TableCell align="center">
                      {onDrillAccount && l.counter_code && (
                        <Tooltip title={`View JEs / pending queue for ${l.counter_code}`}>
                          <IconButton size="small" color="primary" onClick={() => onDrillAccount(l.counter_code, l.counter_name)}>
                            <DrillIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {lines.length === 0 && <Typography variant="body2" color="text.secondary">No activity in this category.</Typography>}
      </CardContent>
    </Card>
  );
}


const acctRows = (accounts) => (accounts || []).map(a => ({ code: a.account_code, name: a.account_name, values: [a.amount_usd] }));
const lineRows = (lines) => (lines || []).map(l => ({ kind: 'line', label: l.label, values: [l.amount_usd], accounts: acctRows(l.accounts) }));

function statementRows(st) {
  const rows = [
    { kind: 'section', label: 'Cash flows from operating activities' },
    { kind: 'line', label: 'Profit / (loss) for the period', values: [st.operating.profit_usd] },
  ];
  if (st.operating.adjustments.length) rows.push({ kind: 'note', label: 'Adjustments for non-cash items:' }, ...lineRows(st.operating.adjustments));
  if (st.operating.working_capital.length) rows.push({ kind: 'note', label: 'Changes in working capital:' }, ...lineRows(st.operating.working_capital));
  rows.push(
    { kind: 'subtotal', label: 'Net cash from / (used in) operating activities', values: [st.operating.net_usd] },
    { kind: 'section', label: 'Cash flows from investing activities' },
    ...lineRows(st.investing.lines),
    { kind: 'subtotal', label: 'Net cash from / (used in) investing activities', values: [st.investing.net_usd] },
    { kind: 'section', label: 'Cash flows from financing activities' },
    ...lineRows(st.financing.lines),
    { kind: 'subtotal', label: 'Net cash from / (used in) financing activities', values: [st.financing.net_usd] },
    { kind: 'subtotal', label: 'Net increase / (decrease) in cash', values: [st.net_change_usd] },
    {
      kind: 'line', label: 'Cash at the beginning of the period', values: [st.opening_cash_usd],
      accounts: (st.cash_accounts || []).map(a => ({ code: a.account_code, name: a.account_name, values: [a.opening_usd] })),
    },
    {
      kind: 'total', label: 'Cash at the end of the period', values: [st.closing_cash_usd],
    },
    {
      kind: 'line', label: 'Cash at the end of the period, by bank account', values: [null],
      accounts: (st.cash_accounts || []).map(a => ({ code: a.account_code, name: a.account_name, values: [a.closing_usd] })),
    },
  );
  return rows;
}

export default function CashFlowStatement() {
  const today = new Date();
  const yearStart = new Date(today.getFullYear(), 0, 1).toISOString().slice(0, 10);
  const yearEnd = new Date(today.getFullYear(), 11, 31).toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(yearStart);
  const [endDate, setEndDate] = useState(yearEnd);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drill, setDrill] = useState(null);
  const [showDirect, setShowDirect] = useState(false);

  // Journal entries behind a line, for the report period, in an overlay.
  const openEntries = (accountCode, accountName) => setDrill({
    accountCode, accountName,
    startDate: data?.period?.startDate || startDate,
    endDate: data?.period?.endDate || endDate,
  });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await generateCashFlowStatement({ start_date: startDate, end_date: endDate });
      if (!r.success) {
        setError(r.error || 'Unknown error');
      } else {
        setData(r.data);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const st = data?.statement;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">Cash Flow Statement</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            type="date" label="From" value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            size="small" InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date" label="To" value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            size="small" InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" startIcon={<Refresh />} onClick={load} disabled={loading}>
            Generate
          </Button>
        </Stack>
      </Stack>

      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      {data && !loading && (
        <>
          {data.tie_check && !data.tie_check.is_balanced && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Does not tie: direct method off by {fmtSigned(data.tie_check.direct_delta_usd)}, indirect method off by {fmtSigned(data.tie_check.indirect_delta_usd)}.
              {data.tie_check.unbalanced_entries_error && (
                <Box sx={{ mt: 1 }}>{data.tie_check.unbalanced_entries_error}.</Box>
              )}
              {(data.tie_check.unbalanced_entries || []).length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {data.tie_check.unbalanced_entries.map((e) => (
                    <li key={e.je_id}>
                      {e.entry_number} ({e.entry_date}): debits − credits = {e.native_diff} {e.base_currency} ({fmtSigned(e.usd_diff)})
                    </li>
                  ))}
                </Box>
              )}
            </Alert>
          )}

          {st && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6">Statement of cash flows</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {data.period.startDate} to {data.period.endDate} · Click a line to see its accounts; the magnifier opens that account's journal entries.
                </Typography>
                <StatementTable columns={[`${data.period.startDate} – ${data.period.endDate}`]} rows={statementRows(st)} onDrill={openEntries} />
                {data.tie_check?.is_balanced && (
                  <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                    Ties: opening cash + net change = closing cash, which agrees to Cash at bank on the Balance Sheet.
                    Ledger cash is not yet fully reconciled to bank statements; see the reconciliation on the Balance Sheet.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          <Button
            onClick={() => setShowDirect(!showDirect)}
            startIcon={showDirect ? <ExpandMore /> : <ChevronRight />}
            sx={{ mb: 1 }}
          >
            Cash movements by counterparty (direct method)
          </Button>
          <Collapse in={showDirect}>
            <CategoryCard title="Operating Activities" icon={<TrendingUp color="primary" />}
                          bucket={data.operating} color={data.operating.net >= 0 ? 'success.main' : 'error.main'}
                          onDrillAccount={openEntries} />
            <CategoryCard title="Investing Activities" icon={<TrendingDown color="warning" />}
                          bucket={data.investing} color={data.investing.net >= 0 ? 'success.main' : 'error.main'}
                          onDrillAccount={openEntries} />
            <CategoryCard title="Financing Activities" icon={<SwapHoriz color="info" />}
                          bucket={data.financing} color={data.financing.net >= 0 ? 'success.main' : 'error.main'}
                          onDrillAccount={openEntries} />
            {data.unclassified && data.unclassified.lines.length > 0 && (
              <CategoryCard title="Unclassified" icon={<SwapHoriz color="error" />}
                            bucket={data.unclassified} color="warning.main"
                            onDrillAccount={openEntries} />
            )}
          </Collapse>

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Currency: {data.currency} (each entry converted at its month's rate) · Brackets are cash outflows.
          </Typography>
        </>
      )}
      <AccountEntriesDialog drill={drill} onClose={() => setDrill(null)} />
    </Box>
  );
}
